<script>
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { useAI } from 'dashboard/composables/useAI';
import AILoader from './AILoader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    AILoader,
    NextButton,
  },
  props: {
    aiOption: {
      type: String,
      required: true,
    },
  },
  emits: ['close', 'applyText'],
  setup() {
    const { formatMessage } = useMessageFormatter();
    const {
      draftMessage,
      processEvent,
      recordAnalytics,
      aiProviders,
      activeAIProviderId,
      setAIProvider,
      providerTranslationKey,
      providerDisplayName,
    } = useAI();
    return {
      draftMessage,
      processEvent,
      recordAnalytics,
      formatMessage,
      aiProviders,
      activeAIProviderId,
      setAIProvider,
      providerTranslationKey,
      providerDisplayName,
    };
  },
  data() {
    return {
      generatedContent: '',
      isGenerating: true,
    };
  },
  computed: {
    headerTitle() {
      const translationKey = this.aiOption?.toUpperCase();
      const providerKey = this.providerTranslationKey || 'OPEN_AI';
      return translationKey
        ? this.$t(`INTEGRATION_SETTINGS.${providerKey}.WITH_AI`, {
            option: this.$t(
              `INTEGRATION_SETTINGS.${providerKey}.OPTIONS.${translationKey}`
            ),
          })
        : '';
    },
    draftTitle() {
      const providerKey = this.providerTranslationKey || 'OPEN_AI';
      return this.$t(
        `INTEGRATION_SETTINGS.${providerKey}.ASSISTANCE_MODAL.DRAFT_TITLE`
      );
    },
    generatedTitle() {
      const providerKey = this.providerTranslationKey || 'OPEN_AI';
      return this.$t(
        `INTEGRATION_SETTINGS.${providerKey}.ASSISTANCE_MODAL.GENERATED_TITLE`
      );
    },
    cancelLabel() {
      const providerKey = this.providerTranslationKey || 'OPEN_AI';
      return this.$t(
        `INTEGRATION_SETTINGS.${providerKey}.ASSISTANCE_MODAL.BUTTONS.CANCEL`
      );
    },
    applyLabel() {
      const providerKey = this.providerTranslationKey || 'OPEN_AI';
      return this.$t(
        `INTEGRATION_SETTINGS.${providerKey}.ASSISTANCE_MODAL.BUTTONS.APPLY`
      );
    },
    aiProviderOptions() {
      return this.aiProviders.map(provider => ({
        label: provider.name,
        value: provider.id,
      }));
    },
    selectedAIProvider: {
      get() {
        return this.activeAIProviderId;
      },
      set(value) {
        this.setAIProvider(value);
      },
    },
    showProviderSelector() {
      return this.aiProviders.length > 1;
    },
  },
  watch: {
    activeAIProviderId() {
      if (this.aiOption) {
        this.generateAIContent(this.aiOption);
      }
    },
  },
  mounted() {
    this.generateAIContent(this.aiOption);
  },

  methods: {
    onClose() {
      this.$emit('close');
    },

    async generateAIContent(type = 'rephrase') {
      this.isGenerating = true;
      this.generatedContent = await this.processEvent(type);
      this.isGenerating = false;
    },
    applyText() {
      this.recordAnalytics(this.aiOption);
      this.$emit('applyText', this.generatedContent);
      this.onClose();
    },
  },
};
</script>

<template>
  <div class="flex flex-col">
    <woot-modal-header :header-title="headerTitle" />
    <form
      class="flex flex-col w-full modal-content"
      @submit.prevent="applyText"
    >
      <div v-if="showProviderSelector" class="w-full mb-4">
        <label class="block text-sm font-medium text-n-slate-11 mb-1">
          {{
            $t('INTEGRATION_SETTINGS.GENERAL.PROVIDER_SELECTOR.LABEL', {
              provider: providerDisplayName || '',
            })
          }}
        </label>
        <woot-select
          v-model="selectedAIProvider"
          :options="aiProviderOptions"
          :placeholder="
            $t('INTEGRATION_SETTINGS.GENERAL.PROVIDER_SELECTOR.PLACEHOLDER')
          "
        />
      </div>
      <div v-if="draftMessage" class="w-full">
        <h4 class="mt-1 text-base text-n-slate-12">{{ draftTitle }}</h4>
        <p v-dompurify-html="formatMessage(draftMessage, false)" />
        <h4 class="mt-1 text-base text-n-slate-12">{{ generatedTitle }}</h4>
      </div>
      <div>
        <AILoader v-if="isGenerating" />
        <p v-else v-dompurify-html="formatMessage(generatedContent, false)" />
      </div>

      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="cancelLabel"
          @click.prevent="onClose"
        />
        <NextButton
          type="submit"
          :disabled="!generatedContent"
          :label="applyLabel"
        />
      </div>
    </form>
  </div>
</template>

<style lang="scss" scoped>
.modal-content {
  @apply pt-2 px-8 pb-8;
}

.container {
  width: 100%;
}
</style>
